import clsx from "clsx";
import { useEffect, useState } from "react";

import { Button, Modal, Select } from "@smart/itops-sb-design-system-dom";
import { useAnalyticsEvent } from "@smart/manage-hooks-dom";

import {
  LanguageKey,
  SupportedLanguageOption,
  defaultSupportedLanguages,
} from "../../../../types";

type TranslationModalProps = {
  open: boolean;
  onClose: () => void;
  onSaveLanguages: (languages: LanguageKey[]) => Promise<void>;
  supportedLanguages?: SupportedLanguageOption[];
  formTitle: string;
};

export const TranslationModal = ({
  open,
  onClose,
  onSaveLanguages,
  supportedLanguages,
  formTitle,
}: TranslationModalProps) => {
  const { publishEvent } = useAnalyticsEvent();
  const [selectedLanguages, setSelectedLanguages] = useState<LanguageKey[]>(
    supportedLanguages?.length ? supportedLanguages.map((sl) => sl.key) : [],
  );
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!open || isSaving) return;
    setSelectedLanguages(
      supportedLanguages?.length ? supportedLanguages.map((sl) => sl.key) : [],
    );
  }, [open, supportedLanguages]);

  const onSave = async () => {
    setIsSaving(true);
    await onSaveLanguages(selectedLanguages);

    publishEvent?.({
      category: "track",
      name: "IntakeLanguagesAdded",
      screen: "FormBuilder",
      entryPoint: "FormBuilderEditView",
      component: "Languages",
      jiraKey: "LINK-3181",
      interactionType: "Active",
      properties: {
        Source: "FormBuilderList, FormBuilderPreview",
        Name: formTitle,
        LanguageCount: selectedLanguages.length,
      },
    }).catch(console.error);
    setIsSaving(false);
    onClose();
  };

  return (
    <Modal
      header={{
        icon: "solidLanguage",
        iconBgColor: "bg-cyan-140",
        text: "Languages",
      }}
      footer={{
        buttons: [
          {
            text: "Cancel",
            variant: "secondarySubtle",
            onClick: onClose,
          },
          {
            text: "Save",
            variant: "primaryDefault",
            onClick: onSave,
          },
        ],
      }}
      open={open}
      onClose={onClose}
    >
      <div className={clsx("flex flex-col gap-medium")}>
        <span className="text-ui">
          Manage available languages for this form.
        </span>
        <p className="text-ui">
          Once selected, the form displays a language dropdown at the top and
          translations can be reviewed and edited. Responses completed in
          another language stay in that language and aren&apos;t translated back
          to English.
        </p>
        <div className="flex flex-col gap-small">
          <Select
            label="Select Languages"
            placeholder="Supported Languages"
            options={defaultSupportedLanguages
              .map((language) => ({
                label: language.label,
                value: language.key,
              }))
              .sort((a, b) => a.label.localeCompare(b.label))}
            value={selectedLanguages}
            onChange={setSelectedLanguages}
            mandatory
            isMultiple
          />
          <div className="flex justify-end">
            <Button
              text="Clear selection"
              variant="inlineDefault"
              onClick={() => setSelectedLanguages([])}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};
